import { motion } from "framer-motion";
import { FaArrowRight, FaPhoneAlt, FaStar, FaShieldAlt, FaUserMd, FaCheckCircle } from "react-icons/fa";
import { COMPANY } from "../data/content";

const Hero = () => {
    const goto = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

    return (
        <section
            id="home"
            data-testid="hero-section"
            className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden"
        >
            {/* Background layers */}
            <div className="absolute inset-0 bg-gradient-to-br from-white via-sky-50/40 to-brand-50/60 dark:from-slate-950 dark:via-slate-950 dark:to-brand-900/20" />
            <div className="absolute inset-0 bg-medical-grid opacity-50" />
            <div className="absolute inset-0 hero-glow" />

            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                >
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass border-brand-200/60 dark:border-brand-700/30 text-xs font-semibold uppercase tracking-[0.2em] text-brand-700 dark:text-brand-300 mb-6">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        Trusted Medical Equipment Partner
                    </div>

                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight text-slate-900 dark:text-white">
                        Trusted{" "}
                        <span className="text-gradient-brand">Medical Equipment</span>
                        {" "}Solutions for Modern Healthcare
                    </h1>

                    <p className="mt-6 text-base sm:text-lg text-slate-600 dark:text-slate-300 leading-relaxed max-w-xl">
                        Premium hospital, surgical, oxygen and biomedical equipment — backed by certified service, installation, calibration and AMC across Puducherry & Tamil Nadu.
                    </p>

                    <div className="mt-8 flex flex-wrap items-center gap-3">
                        <button
                            data-testid="hero-explore-btn"
                            onClick={() => goto("products")}
                            className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-brand-700 to-brand-500 text-white font-semibold shadow-lg shadow-brand-700/25 hover:shadow-xl hover:shadow-brand-700/40 transition-all hover:-translate-y-0.5"
                        >
                            Explore Products
                            <FaArrowRight className="group-hover:translate-x-1 transition-transform" />
                        </button>
                        <button
                            data-testid="hero-contact-btn"
                            onClick={() => goto("contact")}
                            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white font-semibold hover:border-brand-500 hover:text-brand-700 dark:hover:text-brand-300 transition"
                        >
                            <FaPhoneAlt className="text-sm" /> Contact Us
                        </button>
                    </div>

                    {/* Trust row */}
                    <div className="mt-10 flex flex-wrap items-center gap-6 text-sm">
                        <div className="flex items-center gap-2">
                            <div className="flex">
                                {[...Array(5)].map((_, i) => <FaStar key={i} className="text-amber-400" />)}
                            </div>
                            <span className="font-bold text-slate-900 dark:text-white">5.0</span>
                            <span className="text-slate-500 dark:text-slate-400">Google Rating</span>
                        </div>
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                            <FaCheckCircle className="text-emerald-500" /> Authorized Supplier
                        </div>
                        <div className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                            <FaShieldAlt className="text-brand-500" /> 12+ Years Experience
                        </div>
                    </div>
                </motion.div>

                {/* Visual side */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8, delay: 0.15 }}
                    className="relative"
                >
                    <div className="relative aspect-[5/4] rounded-[2.5rem] overflow-hidden shadow-2xl shadow-brand-900/20">
                        <video
                            src="https://www.pexels.com/download/video/6130551/" 
                            className="w-full h-full object-cover scale-125"
                            autoPlay
                            loop
                            muted
                            playsInline
                        />
                        <div className="absolute inset-0 bg-gradient-to-tr from-brand-900/40 via-transparent to-transparent" />
                    </div>

                    {/* Floating stat card 1 */}
                    <motion.div
                        animate={{ y: [0, -10, 0] }}
                        transition={{ duration: 5, repeat: Infinity }}
                        className="absolute -bottom-6 -left-2 sm:-left-8 glass-strong rounded-2xl p-4 shadow-xl flex items-center gap-3 max-w-[220px]"
                    >
                        <div className="w-11 h-11 rounded-xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center">
                            <FaUserMd />
                        </div>
                        <div>
                            <div className="text-xl font-heading font-extrabold text-slate-900 dark:text-white">500+</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400 leading-tight">Hospitals &amp; Clinics Served</div>
                        </div>
                    </motion.div>

                    {/* Floating stat card 2 */}
                    <motion.div
                        animate={{ y: [0, 10, 0] }}
                        transition={{ duration: 5, repeat: Infinity, delay: 0.6 }}
                        className="absolute -top-4 -right-2 sm:-right-6 glass-strong rounded-2xl p-4 shadow-xl"
                    >
                        <div className="flex items-center gap-2">
                            <div className="flex">
                                {[...Array(5)].map((_, i) => <FaStar key={i} className="text-amber-400 text-sm" />)}
                            </div>
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Rated 5.0 by Healthcare Pros</div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};

export default Hero;
